import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createEstimate,
  listEstimates,
  sendEstimate,
  type EstimateRecord,
} from '@/lib/api/portal-staff.js';
import { SlideOver } from '@/components/SlideOver.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import type { TicketRepair } from '@/lib/api/tickets.js';

export function TicketEstimatesPanel({
  ticketId,
  repairs,
}: {
  ticketId: string;
  repairs: TicketRepair[];
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [services, setServices] = useState(
    repairs.map((r) => ({
      serviceName: r.serviceName,
      price: Number(r.quotedPrice ?? 0),
      serviceId: r.serviceId ?? undefined,
    })),
  );
  const [notes, setNotes] = useState('');
  const [validDays, setValidDays] = useState(2);
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [sendVia, setSendVia] = useState<'email' | 'sms' | 'both' | 'none'>('email');

  const estimatesQuery = useQuery({
    queryKey: ['estimates', ticketId],
    queryFn: () => listEstimates({ ticketId, pageSize: 20 }).then((r) => r.data),
  });

  const estimates = (estimatesQuery.data ?? []) as EstimateRecord[];

  const createMutation = useMutation({
    mutationFn: () =>
      createEstimate({
        ticketId,
        services: services.filter((s) => s.serviceName.trim()),
        notes: notes || undefined,
        validDays,
        requiresSignature,
        sendVia,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      setOpen(false);
      toastSuccess('Estimate created');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => sendEstimate(id, 'email'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates', ticketId] });
      toastSuccess('Estimate resent');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const pendingBanner = useMemo(() => {
    const awaiting = estimates.find((e) => e.status === 'sent' || e.status === 'pending');
    const approved = estimates.find((e) => e.status === 'approved');
    if (approved) {
      return (
        <p className="mb-2 rounded-lg bg-emerald-50 px-2 py-1.5 text-xs font-semibold text-emerald-800">
          ✓ Customer approved {approved.estimateNumber}
        </p>
      );
    }
    if (awaiting) {
      return (
        <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1.5 text-xs font-semibold text-amber-800">
          ⚠ Awaiting customer approval — {awaiting.estimateNumber}
        </p>
      );
    }
    return null;
  }, [estimates]);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">📋 Estimates</h2>
        <button
          type="button"
          onClick={() => {
            setServices(
              repairs.map((r) => ({
                serviceName: r.serviceName,
                price: Number(r.quotedPrice ?? 0),
                serviceId: r.serviceId ?? undefined,
              })),
            );
            setOpen(true);
          }}
          className="text-xs font-semibold text-primary"
        >
          + Create Estimate
        </button>
      </div>
      {pendingBanner}
      <div className="space-y-2">
        {estimates.map((e) => (
          <div key={e.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-sm">
            <div className="flex justify-between gap-2">
              <span className="font-semibold">{e.estimateNumber}</span>
              <span className="capitalize text-slate-500">{e.status}</span>
            </div>
            <div className="mt-1 font-semibold">{formatCurrency(e.total)}</div>
            {e.sentVia ? (
              <p className="text-xs text-slate-500">
                Sent via {e.sentVia}
                {e.validUntil ? ` · Expires ${formatDateTime(e.validUntil)}` : ''}
              </p>
            ) : null}
            <div className="mt-2 flex gap-2">
              {(e.status === 'sent' || e.status === 'pending') && (
                <button
                  type="button"
                  className="text-xs font-semibold text-primary"
                  onClick={() => resendMutation.mutate(e.id)}
                >
                  Resend
                </button>
              )}
              {e.approvalLink ? (
                <a
                  href={e.approvalLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-slate-600"
                >
                  View
                </a>
              ) : null}
            </div>
          </div>
        ))}
        {estimates.length === 0 ? (
          <p className="text-xs text-slate-500">No estimates yet.</p>
        ) : null}
      </div>

      <SlideOver title="Create Estimate" open={open} onClose={() => setOpen(false)}>
        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Services</h3>
            {services.map((s, i) => (
              <div key={`${s.serviceName}-${i}`} className="flex gap-2">
                <input
                  className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  value={s.serviceName}
                  onChange={(e) => {
                    const next = [...services];
                    next[i] = { ...s, serviceName: e.target.value };
                    setServices(next);
                  }}
                />
                <input
                  type="number"
                  step="0.01"
                  className="w-24 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  value={s.price}
                  onChange={(e) => {
                    const next = [...services];
                    next[i] = { ...s, price: Number(e.target.value) };
                    setServices(next);
                  }}
                />
              </div>
            ))}
            <button
              type="button"
              className="text-xs font-semibold text-primary"
              onClick={() => setServices([...services, { serviceName: '', price: 0 }])}
            >
              + Add service
            </button>
          </div>

          <label className="block text-sm font-medium">
            Notes for customer
            <textarea
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <label className="block text-sm font-medium">
            Valid for (days)
            <input
              type="number"
              min={1}
              max={30}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={validDays}
              onChange={(e) => setValidDays(Number(e.target.value))}
            />
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={requiresSignature}
              onChange={(e) => setRequiresSignature(e.target.checked)}
            />
            Requires signature
          </label>

          <label className="block text-sm font-medium">
            Send via
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={sendVia}
              onChange={(e) => setSendVia(e.target.value as typeof sendVia)}
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="both">Both</option>
              <option value="none">Don&apos;t send yet</option>
            </select>
          </label>

          <button
            type="button"
            disabled={createMutation.isPending || services.every((s) => !s.serviceName.trim())}
            onClick={() => createMutation.mutate()}
            className="w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            Create Estimate
          </button>
        </div>
      </SlideOver>
    </section>
  );
}
